import ReactDOM from 'react-dom/client';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Layout from './Layout';
import NotFoundPage from './pages/NotFoundPage';
import Login from './pages/Auth/Login';
import Signup from './pages/Auth/Signup';
import User from './pages/User/User';
import ProjectList from './pages/Project/ProjectList';
import ProjectDetails from './pages/Project/ProjectDetails';
import SampleDetails from './pages/Sample/SampleDetails';
import ImageList from './pages/Image/ImageList';
import BackOfficeLayout from './pages/Backoffice/BackOfficeLayout';
import BackOfficeImageList from './pages/Backoffice/BackOfficeImageList';
import BackOfficeSamples from './pages/Backoffice/BackOfficeSamples';
import BackOfficeHide from './pages/Backoffice/BackOfficeHide';
import BackOfficeScanners from './pages/Backoffice/BackOfficeScanners';
import BackOfficeHubs from './pages/Backoffice/BackOfficeHubs';
import BackOfficeProjects from './pages/Backoffice/BackOfficeProjects';
import BackOfficeScannerCrop from './pages/Backoffice/BackOfficeScannerCrop';
import BackOfficeCompute from './pages/Backoffice/BackOfficeCompute';
import BackOfficeTracking from './pages/Backoffice/BackOfficeTracking';
import BackOfficeMonitoring from './pages/Backoffice/BackOfficeMonitoring';
import BackOfficeMonitoringSettings from './pages/Backoffice/BackOfficeMonitoringSettings';
import BackOfficeTransfer from './pages/Backoffice/BackOfficeTransfer';
import UploadPage from './pages/Upload/UploadPage';
import './index.css';
import './i18n';

const root = document.getElementById('root');
const queryClient = new QueryClient();

if (root) {
    ReactDOM.createRoot(root).render(
        <QueryClientProvider client={queryClient}>
            <HashRouter>
                <Routes>
                    <Route element={<Layout />}>
                        <Route
                            index
                            element={<Navigate to="project" replace />}
                        />
                        <Route path="login" element={<Login />} />
                        <Route path="signup" element={<Signup />} />
                        <Route path="user" element={<User />} />
                        <Route path="project">
                            <Route index element={<ProjectList />} />
                            <Route path=":projectId">
                                <Route index element={<ProjectDetails />} />
                                <Route
                                    path=":sampleId"
                                    element={<SampleDetails />}
                                >
                                    <Route index element={<ImageList />} />
                                    {/* Deep link from the annotation tool:
                                        <ts>?x=&y= opens that acquisition
                                        zoomed on that raw-jp2 pixel. Static
                                        siblings above rank higher, so they
                                        keep winning. */}
                                    <Route
                                        path=":acquisitionTs"
                                        element={<ImageList />}
                                    />
                                </Route>
                            </Route>
                        </Route>
                        <Route path="*" element={<NotFoundPage />} />
                    </Route>
                    <Route path="backOffice" element={<BackOfficeLayout />}>
                        <Route
                            index
                            element={<Navigate to="monitoring" replace />}
                        />
                        {/* Monitoring is a section, not a page: it always
                            lands on one of its three tabs. */}
                        <Route path="monitoring">
                            <Route
                                index
                                element={<Navigate to="field" replace />}
                            />
                            <Route
                                path="field"
                                element={<BackOfficeMonitoring site="FIELD" />}
                            />
                            <Route
                                path="bench"
                                element={<BackOfficeMonitoring site="BENCH" />}
                            />
                            <Route
                                path="settings"
                                element={<BackOfficeMonitoringSettings />}
                            />
                        </Route>
                        <Route
                            path="images"
                            element={<BackOfficeImageList />}
                        />
                        <Route
                            path="samples"
                            element={<BackOfficeSamples />}
                        />
                        <Route
                            path="projects"
                            element={<BackOfficeProjects />}
                        />
                        <Route path="hubs" element={<BackOfficeHubs />} />
                        <Route path="scanners">
                            <Route index element={<BackOfficeScanners />} />
                            <Route
                                path=":serialNumber"
                                element={<BackOfficeScannerCrop />}
                            />
                        </Route>
                        <Route path="hide" element={<BackOfficeHide />} />
                        <Route
                            path="compute"
                            element={<BackOfficeCompute />}
                        />
                        <Route
                            path="tracking"
                            element={<BackOfficeTracking />}
                        />
                        <Route
                            path="transfer"
                            element={<BackOfficeTransfer />}
                        />
                    </Route>
                    <Route
                        path="upload/:token"
                        element={<UploadPage />}
                    />
                </Routes>
            </HashRouter>
        </QueryClientProvider>,
    );
} else {
    console.error('Root element not found.');
}
